/**
 * Getting the partner event QR out of the app as a file.
 *
 * The card rendered a QR you could look at and a link you could copy, which is
 * no use when what you need is to send a partner something they can put on a
 * flyer. This is the export half.
 *
 * Two paths, because the one obvious call does not work where it is most
 * needed. `react-native-qrcode-svg`'s `toDataURL` delegates to react-native-svg's
 * NATIVE rasterizer; on web that method does not exist, so an implementation
 * built on it alone would work on a phone and silently do nothing in the
 * browser — which is where admins actually open this screen.
 *
 *   web    the QR is a real <svg> in the DOM. Serialize it, draw it to a
 *          canvas at print size, and download the PNG through a hidden anchor
 *          (the same shape AccountRecoveryCodeSheet uses, for the same reason:
 *          the Web Share API on desktop offers "share this URL", not "save
 *          this file" — see ED-2329).
 *   native `toDataURL` is the right call, then write to cache and hand the
 *          file to the share sheet.
 *
 * Everything here takes its browser globals as arguments rather than reaching
 * for them, so the logic is unit-testable without a DOM. The one genuinely
 * untestable step is the canvas raster itself, kept to a single function.
 */

/** Characters a downloaded filename can carry across macOS, Windows and Linux. */
const FILENAME_SAFE = /[^a-z0-9-]+/gi;

/**
 * `family-resource-network` -> `eleplan-qr-family-resource-network.png`
 *
 * The slug is already url-safe, but it arrives from the database rather than a
 * constant, so it is re-cleaned here instead of trusted.
 */
export function qrFileName(slug: string | null | undefined, ext: 'png' | 'svg' = 'png'): string {
  const clean = String(slug ?? '')
    .trim()
    .replace(FILENAME_SAFE, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
    .slice(0, 60);
  return `eleplan-qr-${clean || 'partner'}.${ext}`;
}

/**
 * react-native-svg's web output is not always a standalone document: the xmlns
 * is implied by the HTML parser and can be missing from the serialized string.
 * An <img> fed a data: URL parses it as XML, where it is required — without it
 * the image fails to load and the export produces a blank PNG rather than an
 * error. Added only when absent so a correct document is left alone.
 */
export function ensureSvgNamespace(markup: string): string {
  if (/\sxmlns=/.test(markup)) return markup;
  return markup.replace(/^(\s*<svg)\b/i, '$1 xmlns="http://www.w3.org/2000/svg"');
}

/** The serialized <svg> for a QR, given the wrapper element it was rendered into. */
export function serializeQrSvg(
  wrapper: { querySelector?: (sel: string) => unknown } | null | undefined,
  serializer: { serializeToString: (node: any) => string },
): string | null {
  const svg = wrapper?.querySelector?.('svg');
  if (!svg) return null;
  const markup = serializer.serializeToString(svg);
  return markup ? ensureSvgNamespace(markup) : null;
}

/** `data:image/png;base64,AAAA` -> `AAAA`. Returns null for anything else. */
export function dataUrlToBase64(dataUrl: string | null | undefined): string | null {
  const match = /^data:[^;,]+;base64,(.+)$/.exec(String(dataUrl ?? ''));
  return match ? match[1] : null;
}

type DownloadDeps = {
  document?: any;
  URL?: { createObjectURL: (b: any) => string; revokeObjectURL: (u: string) => void };
  Blob?: any;
  setTimeout?: (fn: () => void, ms: number) => unknown;
};

/**
 * Browser-download bytes as a file. Returns false when there is no DOM, so the
 * caller can fall through to the native path rather than throw.
 *
 * Mirrors AccountRecoveryCodeSheet: the anchor must be in the document for the
 * click to dispatch a download rather than a navigation in some browsers, and
 * the object URL is revoked a tick later because revoking synchronously
 * cancels the download.
 */
export function downloadBlobWeb(
  bytes: BlobPart,
  mime: string,
  filename: string,
  deps: DownloadDeps = {},
): boolean {
  const doc = deps.document ?? (globalThis as any)?.document;
  const url = deps.URL ?? (globalThis as any)?.URL;
  const BlobCtor = deps.Blob ?? (globalThis as any)?.Blob;
  const later = deps.setTimeout ?? (globalThis as any)?.setTimeout;
  if (!doc?.createElement || !url?.createObjectURL || !BlobCtor) return false;

  const href = url.createObjectURL(new BlobCtor([bytes], { type: mime }));
  const link = doc.createElement('a');
  link.setAttribute('href', href);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  doc.body?.appendChild?.(link);
  link.click();
  doc.body?.removeChild?.(link);
  later?.(() => url.revokeObjectURL(href), 1000);
  return true;
}

/**
 * Raster an SVG string to a PNG data URL at `size` square.
 *
 * The one step that needs a live browser. An inline SVG carrying no external
 * references does not taint the canvas, so toDataURL is allowed to read it
 * back. Rejects rather than resolving blank when the image will not decode.
 */
export function rasterizeSvgToPng(
  svgMarkup: string,
  size: number,
  deps: { document?: any; Image?: any; btoa?: (s: string) => string } = {},
): Promise<string> {
  const doc = deps.document ?? (globalThis as any)?.document;
  const ImageCtor = deps.Image ?? (globalThis as any)?.Image;
  if (!doc?.createElement || !ImageCtor) return Promise.reject(new Error('no_dom'));

  return new Promise((resolve, reject) => {
    const img = new ImageCtor();
    img.onload = () => {
      try {
        const canvas = doc.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('no_canvas_context')); return; }
        // A QR must sit on opaque white: a transparent PNG dropped onto a dark
        // slide inverts the modules and stops scanning.
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err instanceof Error ? err : new Error('raster_failed'));
      }
    };
    img.onerror = () => reject(new Error('svg_decode_failed'));
    // encodeURIComponent rather than base64: it avoids btoa's unicode throw on
    // any non-latin1 byte the serializer might emit.
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
  });
}

/** Print size for the exported PNG. 1024px square scans reliably at flyer scale. */
export const QR_EXPORT_SIZE = 1024;
